import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

const TotalRight = styled.div`
  text-align: end;
  flex-shrink: 0;
`

const Divider = styled.hr`
  width: 100%;
  height: 1px;
  border: 0;
  margin: 0;
  background: ${props => props.theme.palette.neutral.default.six};
`

const CheckoutButton = styled(TextButton)`
  min-height: ${rem(48)};
`

const CheckoutBlockerSummaryWrapper = styled.div`
  padding: ${rem(8)} ${rem(16)} calc(${rem(16)} + env(safe-area-inset-bottom));
  text-align: center;
`

const CheckoutBlockerSummary = styled(BodyText)`
  color: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
`

interface Props {
  totalPrice: number
  totalValue: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  checkoutBlockerItemCount: number
  loyaltyCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest>
  isLoading: boolean
  isCheckoutLoading: boolean
  isLoggedIn: boolean
  onCheckout: () => Promise<void>
}

function CartPanelFooter({
  totalPrice,
  totalValue,
  totalItemCount,
  hasCheckoutReadyItems,
  showReadyItemsOnlyNote,
  checkoutBlockerItemCount,
  loyaltyCalculationRequests,
  isLoading,
  isCheckoutLoading,
  isLoggedIn,
  onCheckout,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleCheckoutClick = useCallback(() => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    onCheckout()
  }, [isLoggedIn, showModal, onCheckout])

  const totalHeading = intl.formatMessage(
    {
      id: 'wrcligwUpGIpkDJ9iUs3',
      defaultMessage: 'Total ({count, plural, one {# item} other {# items}})',
      description: 'Cart panel footer heading showing total with item count',
    },
    { count: totalItemCount },
  )

  const checkoutLabel = !isLoggedIn
    ? intl.formatMessage({
        id: 'PsWbPJA5WZimhO6Dv0Zl',
        defaultMessage: 'Log in to checkout',
        description: 'Cart panel checkout button text when user is not logged in',
      })
    : intl.formatMessage({
        id: '7Ej3duOhiVx-nzIcPorw',
        defaultMessage: 'Book now',
        description: 'Primary checkout button text shown in the trip planner cart panel footer',
      })

  const checkoutStartIcon = useMemo(() => {
    return isCheckoutLoading ? <LoadingSpinner inline size={16} /> : undefined
  }, [isCheckoutLoading])

  const checkoutBlockerSummary = intl.formatMessage(
    {
      id: '72DOtW_TiEpooT_ZgfDH',
      defaultMessage: '{count, plural, one {# item needs details before it can be booked} other {# items need details before they can be booked}}',
      description: 'Cart footer message below checkout explaining how many trip items need more details before booking',
    },
    { count: checkoutBlockerItemCount },
  )
  const shouldShowValue = totalValue > totalPrice

  return (
    <>
      <Sheet padding={24} background="normal-background" border="light-foreground" borderRadius="S">
        <Group direction="vertical" gap={16}>
          <TotalRow>
            <div>
              <Heading variant="heading5">{totalHeading}</Heading>
              <PriceRowTaxesAndFees />
            </div>
            <TotalRight>
              {isLoading && <TextLoadingBox typography="heading5" width="8ch" />}
              {!isLoading && (
                <PriceRowPrice
                  price={totalPrice}
                  priceFormat="preciseDollar"
                  saleUnit=""
                  size="M"
                />
              )}
            </TotalRight>
          </TotalRow>

          {(shouldShowValue || loyaltyCalculationRequests.length > 0) && <Divider />}

          {shouldShowValue && (
            <BodyText variant="medium" weight="bold" colour="success-dark">
              {intl.formatMessage(
                {
                  id: '4BsPZF9CJ9dmUiMOaTxX',
                  defaultMessage: 'This booking is valued up to {totalValue}',
                  description: 'Text showing the total value of the cart booking in the shopping cart',
                },
                { totalValue: <FormatCurrency format="roundedDollar" value={totalValue} /> },
              )}
            </BodyText>
          )}

          {loyaltyCalculationRequests.length > 0 && (
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={loyaltyCalculationRequests}
              calculationType="estimate"
              size="S"
            />
          )}

          {showReadyItemsOnlyNote && checkoutBlockerItemCount === 0 && (
            <BodyText variant="small" colour="neutral-three">
              {intl.formatMessage({
                id: 'hv52vMT3ulBalYfuWSIX',
                defaultMessage: 'Checkout includes ready items only',
                description: 'Cart panel footer note explaining checkout skips items that need user attention',
              })}
            </BodyText>
          )}

          <CheckoutButton
            kind="primary"
            size="large"
            fit="full-width"
            disabled={isLoggedIn && (isLoading || isCheckoutLoading || !hasCheckoutReadyItems)}
            aria-busy={isCheckoutLoading}
            startIcon={checkoutStartIcon}
            onClick={handleCheckoutClick}
          >
            {checkoutLabel}
          </CheckoutButton>
        </Group>
      </Sheet>
      {checkoutBlockerItemCount > 0 && (
        <CheckoutBlockerSummaryWrapper>
          <CheckoutBlockerSummary variant="medium" weight="bold">
            {checkoutBlockerSummary}
          </CheckoutBlockerSummary>
        </CheckoutBlockerSummaryWrapper>
      )}
    </>
  )
}

export default CartPanelFooter
